import { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Music, Info, Check, ChevronsUpDown } from "lucide-react";
import { toast } from "sonner";
import { Device, deviceService } from "@/services/deviceService";
import { cn } from "@/lib/utils";

interface MusicScheduleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  devices: Device[];
  onSuccess?: () => void;
}

const DAYS = [
  { id: "monday", label: "Pazartesi" },
  { id: "tuesday", label: "Salı" },
  { id: "wednesday", label: "Çarşamba" },
  { id: "thursday", label: "Perşembe" },
  { id: "friday", label: "Cuma" },
  { id: "saturday", label: "Cumartesi" },
  { id: "sunday", label: "Pazar" },
];

export default function MusicScheduleDialog({
  open,
  onOpenChange,
  devices,
  onSuccess,
}: MusicScheduleDialogProps) {
  const [startDateTime, setStartDateTime] = useState("");
  const [endDateTime, setEndDateTime] = useState("");
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string>("all");
  const [companyOpen, setCompanyOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);

  // Benzersiz firmaları al
  const companies = useMemo(() => {
    const uniqueCompanies = new Map();
    devices.forEach(device => {
      if (device.company) {
        uniqueCompanies.set(device.company._id, device.company.name);
      }
    });
    return Array.from(uniqueCompanies.entries()).map(([id, name]) => ({ id, name }));
  }, [devices]);

  // Filtrelenmiş firmalar (arama için)
  const filteredCompanies = useMemo(() => {
    if (!searchQuery) return companies;
    return companies.filter(c =>
      c.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [companies, searchQuery]);

  // Filtrelenmiş cihazlar
  const filteredDevices = useMemo(() => {
    const onlineDevices = devices.filter(d => d.isOnline);
    if (selectedCompany === "all") {
      return onlineDevices;
    }
    return onlineDevices.filter(d => d.company?._id === selectedCompany);
  }, [devices, selectedCompany]);

  const handleDayToggle = (dayId: string) => {
    setSelectedDays(prev =>
      prev.includes(dayId)
        ? prev.filter(d => d !== dayId)
        : [...prev, dayId]
    );
  };

  const handleSelectAllDays = () => {
    if (selectedDays.length === DAYS.length) {
      setSelectedDays([]);
    } else {
      setSelectedDays(DAYS.map(d => d.id));
    }
  };

  const handleSubmit = async () => {
    if (!startDateTime || !endDateTime) {
      toast.error("Lütfen başlangıç ve bitiş tarih/saati seçin");
      return;
    }

    if (selectedDays.length === 0) {
      toast.error("Lütfen en az bir gün seçin");
      return;
    }

    if (startDateTime >= endDateTime) {
      toast.error("Bitiş tarihi/saati başlangıçtan sonra olmalıdır");
      return;
    }

    try {
      setLoading(true);
      let successCount = 0;

      for (const device of filteredDevices) {
        try {
          await deviceService.scheduleMusicPlayback(device._id, {
            startDateTime,
            endDateTime,
            days: selectedDays,
          });
          successCount++;
        } catch (error) {
          console.error(`${device.name} müzik zamanlama hatası:`, error);
        }
      }

      toast.success(
        `${successCount} cihaz için müzik zamanlaması oluşturuldu`
      );

      onOpenChange(false);
      resetForm();
      onSuccess?.();
    } catch (error) {
      toast.error("Müzik zamanlama sırasında hata oluştu");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStartDateTime("");
    setEndDateTime("");
    setSelectedDays([]);
    setSelectedCompany("all");
    setSearchQuery("");
  };

  const handleSelectCompany = (companyId: string) => {
    setSelectedCompany(companyId);
    setCompanyOpen(false);
    setSearchQuery("");
  };

  const isFormValid = () => {
    return startDateTime && endDateTime && selectedDays.length > 0 && startDateTime < endDateTime;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Music className="w-5 h-5" />
            Planlı Çalma
          </DialogTitle>
        </DialogHeader>

        {/* Firma Seçimi */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">
            Firma
          </Label>
          <Popover open={companyOpen} onOpenChange={setCompanyOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={companyOpen}
                className="w-full justify-between"
              >
                {selectedCompany === "all"
                  ? "Tüm Firmalar"
                  : companies.find((company) => company.id === selectedCompany)?.name || "Firma seçin"}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
              <div className="flex flex-col">
                {/* Manuel Arama Input */}
                <div className="border-b px-3 py-2">
                  <Input
                    placeholder="Firma ara..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-9"
                  />
                </div>

                {/* Firma Listesi */}
                <div className="max-h-64 overflow-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent hover:scrollbar-thumb-gray-400">
                  {/* Tüm Firmalar */}
                  <div
                    className={cn(
                      "flex items-center px-3 py-2 cursor-pointer hover:bg-gray-100 transition-colors",
                      selectedCompany === "all" && "bg-blue-50"
                    )}
                    onClick={() => handleSelectCompany("all")}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selectedCompany === "all" ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <span className="text-sm">Tüm Firmalar</span>
                  </div>

                  {/* Filtrelenmiş Firmalar */}
                  {filteredCompanies.length === 0 ? (
                    <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                      Firma bulunamadı.
                    </div>
                  ) : (
                    filteredCompanies.map((company) => (
                      <div
                        key={company.id}
                        className={cn(
                          "flex items-center px-3 py-2 cursor-pointer hover:bg-gray-100 transition-colors",
                          selectedCompany === company.id && "bg-blue-50"
                        )}
                        onClick={() => handleSelectCompany(company.id)}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            selectedCompany === company.id ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <span className="text-sm">{company.name}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Bilgilendirme Banner */}
        <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">
              {filteredDevices.length} çevrimiçi cihaza uygulanacak
            </p>
            <p>
              Seçilen günlerde müzik belirtilen saatlerde otomatik olarak başlayacak ve duracaktır.
            </p>
          </div>
        </div>

        {/* Tarih ve Zaman Seçimi */}
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="start-datetime" className="text-sm font-medium">
                Başlangıç Tarih/Saat
              </Label>
              <Input
                id="start-datetime"
                type="datetime-local"
                value={startDateTime}
                onChange={(e) => setStartDateTime(e.target.value)}
                className="w-full"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end-datetime" className="text-sm font-medium">
                Bitiş Tarih/Saat
              </Label>
              <Input
                id="end-datetime"
                type="datetime-local"
                value={endDateTime}
                onChange={(e) => setEndDateTime(e.target.value)}
                className="w-full"
              />
            </div>
          </div>

          {/* Gün Seçimi */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Günler</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleSelectAllDays}
                className="h-8 text-xs"
              >
                {selectedDays.length === DAYS.length ? "Tümünü Kaldır" : "Tümünü Seç"}
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {DAYS.map((day) => (
                <div key={day.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={day.id}
                    checked={selectedDays.includes(day.id)}
                    onChange={() => handleDayToggle(day.id)}
                  />
                  <label
                    htmlFor={day.id}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    {day.label}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Özet */}
          {startDateTime && endDateTime && selectedDays.length > 0 && (
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-sm text-gray-700">
                <span className="font-medium">Özet:</span>{" "}
                <span className="font-medium">{new Date(startDateTime).toLocaleString('tr-TR')}</span> ile{" "}
                <span className="font-medium">{new Date(endDateTime).toLocaleString('tr-TR')}</span> arasında,{" "}
                {selectedDays.length === 7
                  ? "her gün"
                  : selectedDays.map(d => DAYS.find(day => day.id === d)?.label).join(", ")}{" "}
                müzik çalacaktır.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false);
              resetForm();
            }}
            disabled={loading}
          >
            İptal
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isFormValid() || loading}
          >
            {loading ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
